import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription, interval, takeUntil, switchMap, takeWhile, timer, race, map } from 'rxjs';
import { Segment } from '../../../models/segment.model';
import { SegmentService } from '../../../services/segment-api.service';
import { ModalComponent } from '../../../../../shared/components/ui/modal/modal.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { AuditApiService } from '../../../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../../../audit/models/audit.model';
import { AuditTimelineComponent } from '../../../../../shared/components/audit/audit-timeline.component';
import { SegmentMembersTableComponent } from '../segment-members-table/segment-members-table.component';
import { SegmentScheduleSectionComponent } from '../segment-schedule-section/segment-schedule-section.component';
import { SegmentSetOpsSectionComponent } from '../segment-set-ops-section/segment-set-ops-section.component';
import { DatetimePipe } from '../../../../../shared/pipe/datetime.pipe';

@Component({
  selector: 'app-segment-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    AuditTimelineComponent,
    SegmentMembersTableComponent,
    SegmentScheduleSectionComponent,
    SegmentSetOpsSectionComponent,
    DatetimePipe,
  ],
  templateUrl: './segment-detail-modal.component.html',
})
export class SegmentDetailModalComponent implements OnChanges, OnDestroy {

  @Input() isOpen = false;
  @Input() segment: Segment | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() edit = new EventEmitter<number>();
  @Output() memberCountChanged = new EventEmitter<{ segmentId: number; totalContacts: number }>();

  @ViewChild(SegmentMembersTableComponent) membersTable?: SegmentMembersTableComponent;

  isEvaluating = false;
  error: string | null = null;
  isChangingType = false;
  activeTab: 'info' | 'timeline' = 'info';

  auditLogs: AuditLogResponse[] = [];
  isLoadingAudit = false;
  auditError = '';
  auditPage = 0;
  auditTotalPages = 0;
  auditTotalElements = 0;

  private readonly load$ = new Subject<void>();
  private pollSub?: Subscription;
  private readonly POLL_INTERVAL_MS = 2000;
  private readonly POLL_TIMEOUT_MS = 60000;

  constructor(
    private segmentApi: SegmentService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private auditApi: AuditApiService,
  ) { }

  get segmentId(): number {
    return this.segment?.id ?? 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['segment']) {
      if (this.isOpen && this.segment?.id) {
        this.reset();
        this.cargarDatos();
      } else if (!this.isOpen) {
        this.reset();
      }
    }
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.load$.next();
    this.load$.complete();
  }

  private reset(): void {
    this.load$.next();
    this.pollSub?.unsubscribe();
    this.activeTab = 'info';
    this.isEvaluating = false;
    this.error = null;
    this.isChangingType = false;
    this.auditLogs = [];
    this.auditError = '';
    this.auditPage = 0;
    this.auditTotalElements = 0;
  }

  cargarDatos(): void {
    if (!this.segment) return;
    this.error = null;
    this.segmentApi.getSegmentById(this.segmentId).pipe(takeUntil(this.load$)).subscribe({
      next: (res) => { this.segment = res; },
      error: (err) => {
        if (err?.status === 404) { this.handleSegmentGone(); return; }
        this.error = 'No se pudo cargar la información del segmento';
      },
    });
  }

  onMembersChanged(): void {
    this.segmentApi.getSegmentById(this.segmentId).pipe(takeUntil(this.load$)).subscribe({
      next: (res) => {
        this.segment = res;
        this.memberCountChanged.emit({ segmentId: res.id!, totalContacts: res.totalContacts });
      },
      error: (err) => {
        if (err?.status === 404) { this.handleSegmentGone(); return; }
        this.error = 'No se pudo actualizar la información del segmento.';
      },
    });
  }

  handleSegmentGone(): void {
    this.toast.info({
      title: 'Segmento eliminado',
      message: 'El segmento fue eliminado automáticamente al quedar sin miembros activos.',
    });
    this.close.emit();
  }

  onSetOpExecuted(): void {
    this.onMembersChanged();
  }

  // ── Evaluación dinámica ──

  evaluarSegmento(): void {
    if (!this.segment || this.segment.type !== 'DYNAMIC') return;
    this.isEvaluating = true;
    this.error = null;

    this.segmentApi.evaluateSegment(this.segmentId).pipe(takeUntil(this.load$)).subscribe({
      next: () => this.pollSegmentStatus(),
      error: () => {
        this.isEvaluating = false;
        this.error = 'Error al iniciar la evaluación del segmento';
      },
    });
  }

  private pollSegmentStatus(): void {
    this.pollSub?.unsubscribe();

    const timeout$ = timer(this.POLL_TIMEOUT_MS).pipe(map(() => 'TIMEOUT' as const));
    const poll$ = interval(this.POLL_INTERVAL_MS).pipe(
      switchMap(() => this.segmentApi.getSegmentById(this.segmentId)),
      takeWhile(seg => seg.status === 'CALCULATING', true),
    );

    this.pollSub = race(poll$, timeout$).pipe(takeUntil(this.load$)).subscribe({
      next: (res) => {
        if (res === 'TIMEOUT') {
          this.isEvaluating = false;
          this.error = 'La evaluación está tardando más de lo esperado. Refresca para ver el estado.';
          return;
        }
        this.segment = res;
        if (res.status !== 'CALCULATING') {
          this.isEvaluating = false;
          if (res.status === 'READY') {
            this.membersTable?.cargarMiembros();
          } else if (res.status === 'ERROR') {
            this.error = 'La evaluación del segmento falló. Revisa las reglas.';
          }
        }
      },
      error: (err) => {
        this.isEvaluating = false;
        if (err?.status === 404) { this.handleSegmentGone(); return; }
        this.error = 'No se pudo consultar el estado del segmento';
      },
    });
  }

  // ── Conversión de tipo ──

  async cambiarTipoSegmento(): Promise<void> {
    if (!this.segment || this.isChangingType) return;

    const isStaticToDynamic = this.segment.type === 'STATIC';
    const targetType: 'STATIC' | 'DYNAMIC' = isStaticToDynamic ? 'DYNAMIC' : 'STATIC';

    const warningLines = isStaticToDynamic
      ? [
          'Los contactos actuales se conservarán mientras configuras las reglas.',
          'Al evaluar el segmento, los miembros serán reemplazados según las reglas definidas.',
          'El segmento pasará a estado BORRADOR.',
          'Deberás configurar reglas de filtrado desde la edición del segmento.',
        ]
      : [
          'Las reglas de filtrado automático serán eliminadas.',
          'Los contactos actualmente en el segmento se conservarán como miembros estáticos.',
          'El segmento pasará a estado LISTO.',
        ];

    const ok = await this.confirmDialog.confirmDanger({
      title: `Convertir a ${targetType === 'DYNAMIC' ? 'Dinámico' : 'Estático'}`,
      message: `¿Estás seguro de cambiar el tipo de este segmento?\n\n${warningLines.join('\n')}`,
      confirmText: `Sí, convertir a ${targetType === 'DYNAMIC' ? 'Dinámico' : 'Estático'}`,
    });
    if (!ok) return;

    this.isChangingType = true;
    this.segmentApi.changeSegmentType(this.segmentId, targetType).pipe(takeUntil(this.load$)).subscribe({
      next: (updated) => {
        this.segment = updated;
        this.isChangingType = false;
        this.toast.success({
          title: 'Tipo actualizado',
          message: `El segmento ahora es de tipo ${targetType === 'DYNAMIC' ? 'Dinámico' : 'Estático'}.`,
        });
        if (isStaticToDynamic) {
          this.onEdit();
        } else {
          this.membersTable?.cargarMiembros();
        }
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err?.error?.message || 'No se pudo cambiar el tipo del segmento.' });
        this.isChangingType = false;
      },
    });
  }

  // ── Auditoría ──

  setTab(tab: 'info' | 'timeline'): void {
    this.activeTab = tab;
    if (tab === 'timeline' && this.auditLogs.length === 0 && !this.isLoadingAudit) {
      this.cargarAuditoria();
    }
  }

  cargarAuditoria(page: number = 0): void {
    this.isLoadingAudit = true;
    this.auditError = '';
    this.auditPage = page;
    this.auditApi.getEntityTimeline('contact_segment', String(this.segmentId), { page, size: 20 }).pipe(takeUntil(this.load$)).subscribe({
      next: (res) => {
        this.auditLogs = res.content || [];
        this.auditTotalPages = res.totalPages || 0;
        this.auditTotalElements = res.totalElements || 0;
        this.isLoadingAudit = false;
      },
      error: () => {
        this.auditLogs = [];
        this.auditError = 'Error al cargar la línea de tiempo.';
        this.isLoadingAudit = false;
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  onEdit(): void {
    this.edit.emit(this.segmentId);
    this.close.emit();
  }
}
